/*
 * Copyright © 2023 Firecrest Clinical, Ltd.
 * All rights reserved.
 */

import {
    Directive,
    HostListener,
    Input,
    ComponentRef,
    ElementRef,
    Renderer2,
    ComponentFactoryResolver,
    Injector,
    ApplicationRef,
    TemplateRef,
    SimpleChanges,
    OnChanges,
    OnDestroy
} from '@angular/core';
import { FcTooltipComponent, TooltipPosition } from './fc-tooltip.component';

/**
 * FcTooltip directive with Angular.
 */
@Directive({
    selector: '[fcTooltip]'
})
export class FcTooltipDirective implements OnChanges, OnDestroy {
    /**
     * Content of tooltip.
     */
    @Input() fcTooltipContent: string | TemplateRef<any> = '';

    /**
     * Data of tooltip.
     */
    @Input() fcTooltipData: any;

    /**
     * Position of tooltip.
     */
    @Input() fcTooltipPosition: TooltipPosition = 'top';

    /**
     * Tooltip container custom class.
     */
    @Input() tooltipClass = '';

    /**
     * Tooltip only for text ellipsis. Default false.
     */
    @Input() ellipsis = false;

    /**
     * Component ref.
     */
    private fcTooltipComponentRef: ComponentRef<any> | null = null;

    /**
     * Constructor.
     *
     * @param componentFactoryResolver ComponentFactoryResolver.
     * @param injector Injector.
     * @param elementRef ElementRef.
     * @param appRef ApplicationRef.
     * @param renderer Renderer2.
     */
    constructor(
        private componentFactoryResolver: ComponentFactoryResolver,
        private injector: Injector,
        private elementRef: ElementRef,
        private appRef: ApplicationRef,
        private renderer: Renderer2
    ) {}

    /**
     * OnChanges hook.
     * 
     * @param changes SimpleChanges.
     */
    ngOnChanges(changes: SimpleChanges): void {
        const {fcTooltipContent} = changes;
        this.fcTooltipContent = fcTooltipContent.currentValue;
    }

    /**
     * Mouseenter event.
     */
    @HostListener('mouseenter')
    onMouseEnter() {
        this.initializeTooltip();
    }

    /**
     * Mouseleave event.
     */
    @HostListener('mouseleave')
    onMouseLeave() {
        this.destroyTooltip();
    }

    /**
     * Mousemove event.
     * 
     * @param $event Event.
     */
    @HostListener('mousemove', ['$event'])
    onMouseMove($event: MouseEvent) {
        if (this.fcTooltipComponentRef && this.fcTooltipContent && this.fcTooltipPosition === 'pointer') {
            this.fcTooltipComponentRef.instance.left = $event.clientX;
            this.fcTooltipComponentRef.instance.top = $event.clientY;
            this.fcTooltipComponentRef.instance.content = this.fcTooltipContent;
            this.fcTooltipComponentRef.instance.position = this.fcTooltipPosition;
            this.fcTooltipComponentRef.instance.data = this.fcTooltipData;
            this.fcTooltipComponentRef.instance.tooltipClass = this.tooltipClass;
        }
    }

    /**
     * Initiallize tooltip.
     */
    initializeTooltip() {
        if (this.fcTooltipComponentRef || !this.fcTooltipContent) {
            return;
        }
        if (this.ellipsis) {
            if (this.elementRef.nativeElement.scrollWidth <= this.elementRef.nativeElement.clientWidth) {
                return;
            }
        }
        const componentFactory = this.componentFactoryResolver.resolveComponentFactory(FcTooltipComponent);
        this.fcTooltipComponentRef = componentFactory.create(this.injector);
        this.fcTooltipComponentRef.instance.content = this.fcTooltipContent;
        this.fcTooltipComponentRef.instance.position = this.fcTooltipPosition;
        this.fcTooltipComponentRef.instance.data = this.fcTooltipData;
        this.fcTooltipComponentRef.instance.tooltipClass = this.tooltipClass;
        const { left, right, top, bottom } = this.elementRef.nativeElement.getBoundingClientRect();
        switch (this.fcTooltipPosition) {
        case 'bottom': {
            this.fcTooltipComponentRef.instance.left = Math.round((right - left) / 2 + left);
            this.fcTooltipComponentRef.instance.top = Math.round(bottom);
            break;
        }
        case 'top': {
            this.fcTooltipComponentRef.instance.left = Math.round((right - left) / 2 + left);
            this.fcTooltipComponentRef.instance.top = Math.round(top);
            break;
        }
        case 'right': {
            this.fcTooltipComponentRef.instance.left = Math.round(right);
            this.fcTooltipComponentRef.instance.top = Math.round(top + (bottom - top) / 2);
            break;
        }
        case 'left': {
            this.fcTooltipComponentRef.instance.left = Math.round(left);
            this.fcTooltipComponentRef.instance.top = Math.round(top + (bottom - top) / 2);
            break;
        }
        default: {
            break;
        }
        }
        if (this.fcTooltipComponentRef) {
            this.appRef.attachView(this.fcTooltipComponentRef.hostView);
            this.renderer.appendChild(document.body, this.fcTooltipComponentRef.location.nativeElement);
            this.showTooltip();
        }
    }

    /**
     * Show tooltip.
     */
    showTooltip() {
        if (this.fcTooltipComponentRef) {
            this.fcTooltipComponentRef.instance.visible = true;
        }
    }

    /**
     * Destroy tooltip.
     */
    destroyTooltip(): void {
        if (this.fcTooltipComponentRef) {
            this.appRef.detachView(this.fcTooltipComponentRef.hostView);
            this.fcTooltipComponentRef.destroy();
            this.fcTooltipComponentRef = null;
        }
    }

    /**
     * On Destroy hook.
     */
    ngOnDestroy(): void {
        this.destroyTooltip();
    }
}